/**
 * CategoryTab — shared list layout for Cash / Investments / Liabilities.
 * Renders SubTabs, a total row, and the account list with an "Add" CTA.
 *
 * Phase 84.22.10 (Wave B3) — FX helpers read straight from
 * `useCurrencyContext`. Category totals are converted to the user's
 * base currency before summing so a mix of INR/USD/AED accounts
 * produces a meaningful headline; individual rows still show their
 * native currency (see AccountRow).
 */
import React, { useState } from 'react';
import { Plus, Wallet, TrendingUp, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SubTabs from './SubTabs';
import AccountRow from './AccountRow';
import EmptyState from '@/components/EmptyState';
import { formatMoney } from '@/lib/format';
import { ACCOUNTS_TONE } from '@/lib/accountsDemo';
import { useCurrencyContext } from '@/hooks/useCurrencyContext';

const EMPTY_COPY = {
  cash: {
    icon: Wallet,
    title: 'Start with your first cash account',
    body: "Add your salary account, savings, wallets — anything you use to hold money. This is where 2120 tracks your day-to-day balance.",
    ctaLabel: 'Add cash account',
  },
  investments: {
    icon: TrendingUp,
    title: 'Track your first investment',
    body: 'Mutual funds, stocks, ETFs, PPF, or that fixed deposit you keep meaning to log. We\u2019ll fetch live prices for tickers we recognise.',
    ctaLabel: 'Add investment',
  },
  liabilities: {
    icon: CreditCard,
    title: 'Add your first loan or credit card',
    body: "Home loan, car EMI, credit card — anything you're paying down. 2120 will project your payoff date once you tell us the balance.",
    ctaLabel: 'Add liability',
  },
};

export default function CategoryTab({
  category,          // 'cash' | 'investments' | 'liabilities'
  accounts = [],
  subTabs = [],      // [{ id, label, count }]
  addLabel = 'Add account',
  onAdd,
  onSelectAccount,
  onFilterChange,    // Phase 86.H — lets the page scope other sections to this tab
  baseCurrency = 'INR',
}) {
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();
  const changeFilter = (next) => {
    setFilter(next);
    if (onFilterChange) onFilterChange(next);
  };
  const list  = accounts.filter((a) => a.category === category);
  const shown = filter === 'all' ? list : list.filter((a) => a.type === filter);
  const ctx = useCurrencyContext();
  const total = shown.reduce((s, a) => {
    const native = Math.abs(a.balance || 0);
    return s + ctx.toBase(native, a.currency || baseCurrency);
  }, 0);
  const isLiability = category === 'liabilities';
  const isCompletelyEmpty = list.length === 0;
  const emptyCopy = EMPTY_COPY[category] || EMPTY_COPY.cash;

  return (
    <div data-testid={`accounts-tab-${category}`}>
      <SubTabs tabs={subTabs} active={filter} onChange={changeFilter} testidPrefix={`accounts-${category}-filter`} />

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {shown.length} account{shown.length !== 1 ? 's' : ''}
        </p>
        <p
          className="num text-sm font-semibold"
          data-testid={`accounts-${category}-total`}
          style={{ color: isLiability ? ACCOUNTS_TONE.liability : undefined }}
        >
          {/* Phase 84.22.10 — while FX rates are still loading, showing
              a raw sum (which naively adds ₹1M + $20k + AED 8k as if
              they were the same currency) is worse than showing
              nothing. Muted em-dash keeps the layout stable. */}
          {ctx.isReady
            ? `${formatMoney(total, baseCurrency)}${isLiability ? ' total outstanding' : ' total'}`
            : '—'}
        </p>
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
        {isCompletelyEmpty ? (
          // Phase 84.23.1 — categorical empty state with a contextual
          // headline + primary CTA + "Explore Demo Account" secondary
          // so users who don't want to type in real data can still see
          // what the app does with data.
          <EmptyState
            icon={emptyCopy.icon}
            title={emptyCopy.title}
            body={emptyCopy.body}
            primaryCta={{
              label: emptyCopy.ctaLabel,
              onClick: onAdd,
              testid: `accounts-${category}-empty-primary`,
            }}
            secondaryCta={{
              label: 'Explore Demo Account',
              onClick: () => navigate('/settings?tab=demo'),
              testid: `accounts-${category}-empty-secondary`,
            }}
            testid={`accounts-${category}-empty`}
            className="border-0 shadow-none"
          />
        ) : shown.length === 0 ? (
          <div className="px-5 py-10 text-center" data-testid="accounts-empty">
            <p className="text-sm text-muted-foreground">
              No accounts match this filter. Try another sub-tab.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border/40">
            {shown.map((a) => (
              <AccountRow
                key={a.id}
                account={a}
                onClick={() => onSelectAccount?.(a)}
                baseCurrency={baseCurrency}
              />
            ))}
          </div>
        )}
        {!isCompletelyEmpty && (
          <div className="px-5 py-3 border-t border-border/60 bg-offwhite/40">
            <button
              type="button"
              onClick={onAdd}
              className="flex items-center gap-2 text-xs text-muted-foreground hover:text-evergreen font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
              data-testid={`accounts-${category}-add`}
            >
              <Plus className="h-3.5 w-3.5" />
              {addLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
